import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Lock, Mail, ArrowRight, Check } from "lucide-react";

const KEY = "rf_opps_access";

function GateCard({ onGranted }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success
  const [already, setAlready] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    setError("");
    try {
      const { data } = await api.post("/newsletter/access", { email });
      setAlready(!!data.already_subscribed);
      setState("success");
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setState("idle");
    }
  };

  if (state === "success") {
    return (
      <div data-testid="gate-success" className="bg-white brutal-border brutal-shadow max-w-md w-full p-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-pink border-2 border-black mb-5">
          <Check size={28} />
        </div>
        <h2 className="font-display font-black uppercase tracking-tighter text-3xl mb-2">
          {already ? "Welcome back" : "You're in!"}
        </h2>
        <p className="text-black/70 mb-6">
          {already
            ? "You're already part of the Raw Femme community — enjoy the directory."
            : "You've joined the Raw Femme newsletter. Check your inbox to confirm."}
        </p>
        <button
          data-testid="gate-enter-btn"
          onClick={onGranted}
          className="w-full label-caps bg-black text-white py-3 hover:bg-pink hover:text-black transition-colors inline-flex items-center justify-center gap-2"
        >
          Enter the directory <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="gate-card" className="bg-white brutal-border brutal-shadow max-w-md w-full p-8">
      <div className="inline-flex items-center justify-center w-14 h-14 bg-pink border-2 border-black mb-5">
        <Lock size={26} />
      </div>
      <p className="label-caps text-pink mb-2">Raw Femme Opportunities</p>
      <h2 className="font-display font-black uppercase tracking-tighter text-3xl mb-3 leading-none">
        Join the Raw Femme community and unlock opportunities made for you.
      </h2>
      <p className="text-black/70 mb-6">
        Discover grants, residencies, open calls, and more with a focus on women and LGBTQ+ artists. Updated monthly with 🖤
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/50" />
          <input
            data-testid="gate-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full brutal-border pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink"
          />
        </div>
        {error && <p data-testid="gate-error" className="text-sm text-red-600">{error}</p>}
        <button
          data-testid="gate-submit-btn"
          type="submit"
          disabled={state === "loading"}
          className="w-full label-caps bg-black text-white py-3 hover:bg-pink hover:text-black transition-colors disabled:opacity-50"
        >
          {state === "loading" ? "Checking…" : "Unlock Access"}
        </button>
      </form>
      <p className="text-xs text-black/40 mt-4">
        Already subscribed? Enter your email and you'll go straight in. No spam, ever. Only girl power energy.
      </p>
      <p className="text-xs text-black/50 mt-3 pt-3 border-t-2 border-black/10">
        Running an open call or opportunity?{" "}
        <Link to="/submit" data-testid="gate-submit-link" className="font-bold text-pink hover:underline">Submit it here →</Link>
      </p>
    </div>
  );
}

export function AccessGate({ children }) {
  const [granted, setGranted] = useState(() => {
    try { return localStorage.getItem(KEY) === "true"; } catch { return false; }
  });

  if (granted) return children;

  const grant = () => {
    try { localStorage.setItem(KEY, "true"); } catch { /* ignore */ }
    setGranted(true);
  };

  return (
    <div data-testid="access-gate" className="relative min-h-screen">
      <div aria-hidden className="pointer-events-none blur-md select-none h-screen overflow-hidden">
        {children}
      </div>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
        <GateCard onGranted={grant} />
      </div>
    </div>
  );
}
